import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  Globe, 
  Shield, 
  Zap, 
  Server, 
  Users, 
  BarChart3, 
  CheckCircle, 
  ArrowRight,
  Menu,
  X,
  MapPin,
  Clock,
  Award
} from 'lucide-react';


const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  useEffect(() => {
    const handleScroll = () => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMenuOpen]);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-dark-50 text-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed top-0 w-full z-50 bg-dark-50/80 backdrop-blur-md border-b border-dark-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-primary-400">Proxied.tech</span>
            </div>
            
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a href="#home" className="text-gray-300 hover:text-primary-400 transition-colors">Home</a>
                <a href="#features" className="text-gray-300 hover:text-primary-400 transition-colors">Features</a>
                <a href="#services" className="text-gray-300 hover:text-primary-400 transition-colors">Services</a>
                <a href="#pricing" className="text-gray-300 hover:text-primary-400 transition-colors">Pricing</a>
                <button className="bg-primary-600 hover:bg-primary-700 px-4 py-2 rounded-lg transition-colors">
                  Get Started
                </button>
              </div>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-300 hover:text-white"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-dark-100 border-t border-dark-200"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a href="#home" className="block px-3 py-2 text-gray-300 hover:text-primary-400">Home</a>
              <a href="#features" className="block px-3 py-2 text-gray-300 hover:text-primary-400">Features</a>
              <a href="#services" className="block px-3 py-2 text-gray-300 hover:text-primary-400">Services</a>
              <a href="#pricing" className="block px-3 py-2 text-gray-300 hover:text-primary-400">Pricing</a>
              <button className="w-full text-left bg-primary-600 hover:bg-primary-700 px-3 py-2 rounded-lg transition-colors">
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </motion.nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/20 to-dark-50/80 z-10" />
          <img 
            src="/images/homepage.avif" 
            alt="Background" 
            className="w-full h-full object-cover opacity-30"
          />
        </motion.div>
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="space-y-8"
          >
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-7xl font-bold leading-tight"
            >
              Premium <span className="text-primary-400">Proxy</span> Services
              <br />
              <span className="text-3xl md:text-5xl text-gray-300">IPv4 & IPv6 Solutions</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto"
            >
              Access the internet without limits. High-performance proxy services with 
              global coverage across 195+ countries for IPv4 residential proxies.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <button className="bg-primary-600 hover:bg-primary-700 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 hover:scale-105 flex items-center gap-2">
                Start Free Trial <ArrowRight size={20} />
              </button>
              <button className="border border-primary-400 text-primary-400 hover:bg-primary-400 hover:text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300">
                View Pricing
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating stats */}
        <motion.div 
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex gap-8 text-center"
        >
          <div className="bg-dark-100/80 backdrop-blur-sm px-6 py-4 rounded-lg">
            <div className="text-2xl font-bold text-primary-400">195+</div>
            <div className="text-sm text-gray-400">Countries</div>
          </div>
          <div className="bg-dark-100/80 backdrop-blur-sm px-6 py-4 rounded-lg">
            <div className="text-2xl font-bold text-primary-400">99.9%</div>
            <div className="text-sm text-gray-400">Uptime</div>
          </div>
          <div className="bg-dark-100/80 backdrop-blur-sm px-6 py-4 rounded-lg">
            <div className="text-2xl font-bold text-primary-400">24/7</div>
            <div className="text-sm text-gray-400">Support</div>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-dark-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Choose <span className="text-primary-400">Proxied.tech</span>?
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Industry-leading proxy infrastructure with unmatched performance and reliability
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              {
                icon: <Globe className="w-8 h-8" />,
                title: "Global Coverage",
                description: "Access proxies from 195+ countries with IPv4 residential networks",
                image: "/images/earth_location.png"
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: "Maximum Security",
                description: "Bank-grade encryption and anonymous browsing protection",
                image: null
              },
              {
                icon: <Zap className="w-8 h-8" />,
                title: "Lightning Fast",
                description: "Ultra-low latency connections with premium bandwidth",
                image: null
              },
              {
                icon: <Server className="w-8 h-8" />,
                title: "Premium Infrastructure",
                description: "Enterprise-grade datacenter and residential proxy networks",
                image: "/images/datacenter.webp"
              },
              {
                icon: <Clock className="w-8 h-8" />,
                title: "99.9% Uptime SLA",
                description: "Guaranteed reliability with industry-leading service agreements",
                image: "/images/sla.svg"
              },
              {
                icon: <Award className="w-8 h-8" />,
                title: "Expert Support",
                description: "24/7 technical support from proxy infrastructure specialists",
                image: "/images/group-18.png"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-dark-200 p-6 rounded-xl hover:bg-dark-300 transition-all duration-300 hover:scale-105"
              >
                {feature.image && (
                  <div className="mb-4 h-32 overflow-hidden rounded-lg">
                    <img 
                      src={feature.image} 
                      alt={feature.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="text-primary-400 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-dark-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-primary-400">Proxy Services</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Choose from our comprehensive range of proxy solutions tailored to your needs
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {[
              {
                title: "IPv4 Residential",
                description: "Premium residential IP addresses from real devices",
                features: [
                  "195+ Countries Available",
                  "High Anonymity Level",
                  "Rotating IP Addresses",
                  "Real Device IPs",
                  "City & State Targeting"
                ],
                price: "Starting at $5/GB",
                popular: true
              },
              {
                title: "IPv4 Datacenter",
                description: "High-speed datacenter proxies for intensive tasks",
                features: [
                  "Ultra-Fast Speeds",
                  "Dedicated IPs Available",
                  "99.9% Uptime Guarantee",
                  "Instant Activation",
                  "Multiple Subnets"
                ],
                price: "Starting at $2/GB",
                popular: false
              },
              {
                title: "IPv6 Datacenter",
                description: "Next-generation IPv6 proxy infrastructure",
                features: [
                  "Future-Proof Technology",
                  "Unlimited Bandwidth Options",
                  "Premium IPv6 Ranges",
                  "Advanced Protocol Support",
                  "Enterprise-Ready"
                ],
                price: "Starting at $3/GB",
                popular: false
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`relative bg-dark-200 p-8 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${
                  service.popular 
                    ? 'border-primary-400 shadow-lg shadow-primary-400/20' 
                    : 'border-dark-300 hover:border-primary-400'
                }`}
              >
                {service.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary-400 text-dark-50 px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <h3 className="text-2xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-400 mb-6">{service.description}</p>
                
                <div className="mb-6">
                  <div className="text-3xl font-bold text-primary-400">{service.price}</div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                  service.popular
                    ? 'bg-primary-600 hover:bg-primary-700 text-white'
                    : 'border border-primary-400 text-primary-400 hover:bg-primary-400 hover:text-white'
                }`}>
                  Get Started
                </button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-dark-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center"
          >
            {[
              { number: "10M+", label: "IP Addresses", icon: <Globe className="w-8 h-8" /> },
              { number: "195+", label: "Countries", icon: <MapPin className="w-8 h-8" /> },
              { number: "99.9%", label: "Uptime", icon: <BarChart3 className="w-8 h-8" /> },
              { number: "50K+", label: "Happy Clients", icon: <Users className="w-8 h-8" /> }
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-dark-200 p-6 rounded-xl"
              >
                <div className="text-primary-400 mb-4 flex justify-center">{stat.icon}</div>
                <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-800 to-primary-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied customers and experience the power of premium proxy services
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-primary-800 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 hover:scale-105">
                Start Free Trial
              </button>
              <button className="border border-white text-white hover:bg-white hover:text-primary-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300">
                Contact Sales
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-primary-400 mb-4">Proxied.tech</h3>
              <p className="text-gray-400 mb-4">
                Premium proxy services for businesses and individuals worldwide.
              </p>
              <div className="flex space-x-4">
                {/* Social media icons would go here */}
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/ipv4-residential" className="hover:text-primary-400 transition-colors">IPv4 Residential</a></li>
                <li><a href="/ipv4-datacenter" className="hover:text-primary-400 transition-colors">IPv4 Datacenter</a></li>
                <li><a href="/ipv6-datacenter" className="hover:text-primary-400 transition-colors">IPv6 Datacenter</a></li>
                <li><a href="/enterprise" className="hover:text-primary-400 transition-colors">Enterprise Solutions</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/docs" className="hover:text-primary-400 transition-colors">Documentation</a></li>
                <li><a href="/api" className="hover:text-primary-400 transition-colors">API Reference</a></li>
                <li><a href="/support" className="hover:text-primary-400 transition-colors">Contact Support</a></li>
                <li><a href="/status" className="hover:text-primary-400 transition-colors">Status Page</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/about" className="hover:text-primary-400 transition-colors">About Us</a></li>
                <li><a href="/privacy" className="hover:text-primary-400 transition-colors">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-primary-400 transition-colors">Terms of Service</a></li>
                <li><a href="/contact" className="hover:text-primary-400 transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-dark-300 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Proxied.tech. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App; 